import { useState, type ChangeEvent } from "react";
import { Box, Button, Checkbox } from "@mui/material";
import { useLocation } from "react-router-dom";
import SsSelect from "./SsSelect";
import PrefectureSelect from "../../../../../components/PrefectureSelect";
import DivisionSelect from "./DivisionSelect";
import SnackbarCustom, {
  type SnackbarData,
} from "../../../../../components/SnackBarCustom";
import PopoverCustom from "../../../../../components/PopoverCustom";
import { createDisasterManualTrigger } from "../../../../../api";
import type { CreateDisasterManualTriggerPayload } from "../../../../../types/disaster";

type ManualRegistrationProps = {
  onProcessComplete?: (status: SnackbarData) => void;
};

const ManualRegistration = ({ onProcessComplete }: ManualRegistrationProps) => {
  const searchParams = new URLSearchParams(useLocation().search);
  const disasterId = searchParams.get("disaster-id");

  const [postContents, setPostContents] =
    useState<CreateDisasterManualTriggerPayload>({
      disaster_id: disasterId ? Number(disasterId) : null,
      pref_code: null,
      m_division_code: null,
      site_id: null,
      is_attached_file: 1,
    });

  const handleChangePref = (value: string | null) => {
    setPostContents((prev) => ({
      ...prev,
      pref_code: value,
    }));
  };

  const handleChangeDivision = (value: string | null) => {
    setPostContents((prev) => ({
      ...prev,
      m_division_code: value,
    }));
  };

  const handleChangeSites = (value: number[]) => {
    setPostContents((prev) => ({
      ...prev,
      site_id: value.length > 0 ? value : null,
    }));
  };

  const handleChangeIsAttachedFile = (e: ChangeEvent<HTMLInputElement>) => {
    setPostContents((prev) => ({
      ...prev,
      is_attached_file: e.target.checked ? 1 : 0,
    }));
  };

  const [snackBarstatus, setSnackBarstatus] = useState<SnackbarData>();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [confirmPopoverOpen, setPopoverSplitOpen] = useState(false);

  const postDisaster = async (isSuccess: boolean) => {
    if (!isSuccess) {
      return;
    }
    try {
      const status = await createDisasterManualTrigger(postContents);
      setSnackBarstatus(status);
      setSnackbarOpen(true);
      onProcessComplete?.(status);
    } catch (error) {
      console.error("API呼び出しエラー", error);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "fit-content",
        justifyContent: "center",
        gap: "16px",
        paddingRight: "40px",
        paddingLeft: "40px",
        "@media screen and (max-width:900px)": { padding: "16px" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          width: "100%",
          gap: "16px",
          "@media screen and (max-width:900px)": { flexDirection: "column" },
        }}
      >
        <PrefectureSelect
          changedValue={(prefCodes) =>
            handleChangePref(prefCodes.length > 0 ? prefCodes[0] : null)
          }
          isMultiple={false}
          isDisasterId={false}
        />
        <DivisionSelect
          prefCode={
            typeof postContents.pref_code === "string"
              ? postContents.pref_code
              : null
          }
          changedValue={handleChangeDivision}
        />
        <SsSelect
          prefCode={
            typeof postContents.pref_code === "string"
              ? postContents.pref_code
              : null
          }
          divisionCode={postContents.m_division_code ?? null}
          changedValue={handleChangeSites}
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Checkbox
          name="recoveryChecklistAttachCheckbox"
          size="small"
          checked={postContents.is_attached_file === 1}
          onClick={(e) => e.stopPropagation()}
          onChange={handleChangeIsAttachedFile}
          sx={{ cursor: "pointer", pointerEvents: "auto" }}
        />
        SS復旧チェックリスト添付する
      </Box>

      <Button
        name="postButton"
        variant="contained"
        onClick={() => setPopoverSplitOpen(true)}
        sx={{ width: "48px" }}
      >
        登録
      </Button>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          minWidth: "min-content",
          paddingLeft: "40px",
          fontSize: 16,
          fontWeight: "bold",
          color: "#000",
          whiteSpace: "nowrap",
          "@media screen and (max-width:900px)": { padding: "0" },
        }}
      >
        災害エリア追加登録準備中
      </Box>
      <SnackbarCustom
        data={snackBarstatus}
        openStatus={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
      />
      <PopoverCustom
        open={confirmPopoverOpen}
        onClose={() => setPopoverSplitOpen(false)}
        yesNo={postDisaster}
        title="登録確認"
        contents="登録してもよろしいですか？"
      />
    </Box>
  );
};

export default ManualRegistration;
